import { useState } from 'react';
import {
  AlertCircle,
  AlertTriangle,
  ChevronDown,
  ChevronRight,
  CircleCheck,
  CornerDownRight,
  Loader2,
  Wrench,
} from 'lucide-react';
import type { InteractionMessageResponse } from '@vagusx/clients';
import { cn } from '@/lib/utils';

/**
 * Visual variants for the chip.
 *   - `pending`      — waiting for response (spinner)
 *   - `error`        — tool threw / wrapper recorded status='error' (red)
 *   - `softFailure`  — status='ok' but result.ok===false (yellow). The
 *                      tool didn't fail, but reported a non-success
 *                      outcome the model had to recover from (e.g.
 *                      offer_slots → no_matching_slots, confirm_slots →
 *                      no_slot_offered). Distinct from `error` because
 *                      the model handled it normally; distinct from
 *                      `success` because something didn't go to plan.
 *   - `silentReturn` — status='ok' with result.silentReturn===true.
 *                      Tool succeeded AND suppressed the framework's
 *                      auto-reply because the next task drives the
 *                      next utterance. v4-specific; see SILENT-RETURN
 *                      PATTERN in apps/livekit/src/v3/v4-queue/tools.ts.
 *   - `success`      — normal success (green check)
 */
type Variant = 'pending' | 'error' | 'softFailure' | 'silentReturn' | 'success';

function resolveVariant(response: InteractionMessageResponse | undefined): Variant {
  if (!response) return 'pending';
  if (response.kind !== 'tool_response') return 'pending';
  if (response.toolStatus === 'error') return 'error';
  // Inspect the structured result. `silentReturn` is set by v4's
  // wrapper; `ok: false` is the codebase-wide soft-failure convention.
  const payload = response.payload as { result?: unknown } | undefined;
  const result = payload?.result;
  if (result && typeof result === 'object') {
    const r = result as Record<string, unknown>;
    if (r.silentReturn === true) return 'silentReturn';
    if (r.ok === false) return 'softFailure';
  }
  return 'success';
}

/**
 * A collapsible tool-call chip. Variants that warrant attention
 * (error, softFailure) expand by default so the result is visible
 * without a click; success and silentReturn stay collapsed.
 */
export function ToolChip({
  request,
  response,
}: {
  request: InteractionMessageResponse;
  response: InteractionMessageResponse | undefined;
}): React.ReactElement {
  const variant = resolveVariant(response);
  const [expanded, setExpanded] = useState(variant === 'error' || variant === 'softFailure');

  const duration = response?.kind === 'tool_response' ? response.toolDurationMs : null;
  const args = request.kind === 'tool_request' ? (request.payload?.arguments ?? {}) : {};
  const resultPayload = response?.kind === 'tool_response' ? response.payload : undefined;
  const hasResult = resultPayload && 'result' in resultPayload;
  const hasError = resultPayload && 'error' in resultPayload;

  return (
    <div
      className={cn(
        'self-start rounded-md border text-xs transition-colors',
        variant === 'error' && 'border-red-200 bg-red-50 dark:border-red-900 dark:bg-red-950',
        variant === 'softFailure' &&
          'border-amber-200 bg-amber-50 dark:border-amber-900 dark:bg-amber-950',
        (variant === 'success' || variant === 'silentReturn' || variant === 'pending') &&
          'border-border bg-muted/40'
      )}
    >
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="flex w-full items-center gap-2 px-2.5 py-1.5 font-mono text-[11px]"
      >
        {expanded ? (
          <ChevronDown className="h-3 w-3 shrink-0 text-muted-foreground" />
        ) : (
          <ChevronRight className="h-3 w-3 shrink-0 text-muted-foreground" />
        )}
        <Wrench className="h-3 w-3 shrink-0 text-muted-foreground" />
        <span className="font-semibold">{request.toolName ?? 'tool'}</span>
        {variant === 'pending' ? (
          <span className="flex items-center gap-1 text-muted-foreground">
            <Loader2 className="h-3 w-3 animate-spin" />
            pending
          </span>
        ) : (
          <>
            {duration != null && <span className="text-muted-foreground">{duration}ms</span>}
            <StatusIcon variant={variant} />
            {variant === 'silentReturn' && (
              <span className="text-[10px] uppercase tracking-wide text-muted-foreground">
                silent → next task
              </span>
            )}
            {variant === 'softFailure' && (
              <span className="text-[10px] uppercase tracking-wide text-amber-700 dark:text-amber-400">
                no match
              </span>
            )}
          </>
        )}
      </button>
      {expanded && (
        <div className="space-y-2 border-t px-2.5 py-2 font-mono text-[11px]">
          <div>
            <div className="mb-0.5 text-muted-foreground">Request</div>
            <pre className="overflow-auto whitespace-pre-wrap break-all rounded bg-background p-2 text-foreground">
              {safeStringify(args)}
            </pre>
          </div>
          {variant === 'pending' && (
            <div className="italic text-muted-foreground">Waiting for response…</div>
          )}
          {hasResult && variant !== 'silentReturn' && (
            <div>
              <div className="mb-0.5 text-muted-foreground">Response</div>
              <pre className="overflow-auto whitespace-pre-wrap break-all rounded bg-background p-2 text-foreground">
                {safeStringify((resultPayload as { result: unknown }).result)}
              </pre>
            </div>
          )}
          {variant === 'silentReturn' && (
            // No raw {"silentReturn": true} dump — the chip label already
            // says what happened, and the JSON is noise.
            <div className="italic text-muted-foreground">
              Tool succeeded; the next task drives the next utterance.
            </div>
          )}
          {hasError && (
            <div>
              <div className="mb-0.5 text-red-600">Error</div>
              <pre className="overflow-auto whitespace-pre-wrap break-all rounded bg-background p-2 text-red-700">
                {(resultPayload as { error: string }).error}
              </pre>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function StatusIcon({ variant }: { variant: Variant }): React.ReactElement | null {
  switch (variant) {
    case 'error':
      return <AlertCircle className="h-3 w-3 text-red-500" />;
    case 'softFailure':
      return <AlertTriangle className="h-3 w-3 text-amber-500" />;
    case 'silentReturn':
      return <CornerDownRight className="h-3 w-3 text-muted-foreground" />;
    case 'success':
      return <CircleCheck className="h-3 w-3 text-emerald-600" />;
    case 'pending':
      return null;
  }
}

function safeStringify(value: unknown): string {
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
}
